import React from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import Logo from "../../components/Logo";
import {
  LayoutDashboard, BookText, Tag, ShoppingBag, Users, MessageSquareText,
  Briefcase, Settings, LogOut, ExternalLink, Home, FileText, Mail, Package, Image as ImageIcon,
} from "lucide-react";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/products", label: "Products", icon: BookText },
  { to: "/admin/bundles", label: "Bundles", icon: Package },
  { to: "/admin/categories", label: "Categories", icon: Tag },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/blogs", label: "Blog", icon: FileText },
  { to: "/admin/images", label: "Image library", icon: ImageIcon },
  { to: "/admin/pages", label: "Site content", icon: FileText },
  { to: "/admin/newsletter", label: "Newsletter", icon: Mail },
  { to: "/admin/reviews", label: "Reviews", icon: MessageSquareText },
  { to: "/admin/crm", label: "CRM", icon: Briefcase },
  { to: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="w-60 bg-sidebar shrink-0 flex flex-col min-h-screen sticky top-0" data-testid="admin-sidebar">
        <div className="px-6 py-7 border-b border-sidebar">
          <Link to="/admin" className="flex items-center gap-2.5">
            <img src="/logo.svg" alt="" width={28} height={28} />
            <div className="font-display tracking-[0.15em] uppercase text-[13px] text-gold leading-tight">
              Soft Spine<br /><span className="text-sidebar-muted">Press</span>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-0.5">
          {NAV.map((n) => (
            <NavLink
              key={n.to} to={n.to} end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors ${
                  isActive ? "bg-[hsl(270_25%_18%)] text-gold" : "text-sidebar-muted hover:text-gold hover:bg-[hsl(270_25%_15%)]"
                }`
              }>
              <n.icon className="h-4 w-4" strokeWidth={1.5} />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-sidebar p-3 space-y-1">
          <Link to="/" className="flex items-center gap-3 px-3 py-2.5 text-xs text-sidebar-muted hover:text-gold transition-colors" data-testid="admin-view-site">
            <Home className="h-3.5 w-3.5" strokeWidth={1.5} /> View public site
          </Link>
          <button onClick={async () => { await logout(); navigate("/admin/login"); }} className="w-full flex items-center gap-3 px-3 py-2.5 text-xs text-sidebar-muted hover:text-destructive transition-colors" data-testid="admin-logout">
            <LogOut className="h-3.5 w-3.5" strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </aside>
      <div className="flex-1 min-w-0">
        <div className="h-14 border-b border-border bg-background/95 backdrop-blur sticky top-0 z-30 flex items-center justify-between px-8" data-testid="admin-topbar">
          <div className="text-sm text-muted-foreground">
            <span className="italic">Welcome,</span> <span className="font-medium">{user?.name || user?.email}</span>
          </div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Administration</div>
        </div>
        <main className="p-8 max-w-7xl">{children}</main>
      </div>
    </div>
  );
}
